
import React from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { ChartContainer, ChartTooltip, ChartTooltipContent } from "@/components/ui/chart";
import { Bar, BarChart, Cell, Pie, PieChart, ResponsiveContainer, XAxis, YAxis } from "recharts";
import { MessageSquare, Search, ThumbsUp, ThumbsDown, FileText, BrainCircuit } from "lucide-react";

interface AIInteractionsProps {
  timeframe: "7d" | "30d" | "90d" | "1y";
}

// Sample data
const topQuestionsData = [
  { 
    question: "What are the main points about renewable energy subsidies?",
    article: "The Future of Renewable Energy in Urban Environments",
    count: 42,
    satisfaction: 92
  },
  { 
    question: "Can you explain how AI is used in diagnosing cancer?",
    article: "Artificial Intelligence: Transforming Healthcare",
    count: 38,
    satisfaction: 95
  },
  { 
    question: "What economic impacts of climate change are most concerning?",
    article: "Climate Change: The Economic Impact",
    count: 35,
    satisfaction: 88
  },
  { 
    question: "How many digital nomads are there globally after COVID?",
    article: "The Rise of Digital Nomads in Post-Pandemic Era",
    count: 29,
    satisfaction: 85
  },
  { 
    question: "What countries have the strictest crypto regulations?",
    article: "Cryptocurrency Regulations: A Global Perspective",
    count: 26,
    satisfaction: 90
  }
];

const questionTypeData = [
  { name: "Clarification", value: 35, color: "#F97316" },
  { name: "Summary", value: 25, color: "#2563EB" },
  { name: "Additional Context", value: 20, color: "#10B981" },
  { name: "Opinion/Analysis", value: 15, color: "#8B5CF6" },
  { name: "Fact Check", value: 5, color: "#EC4899" }
];

const questionsByArticleData = [
  { article: "Climate Change", questions: 156 },
  { article: "AI in Healthcare", questions: 142 },
  { article: "Crypto Regulations", questions: 98 },
  { article: "Digital Nomads", questions: 87 },
  { article: "Mental Health", questions: 76 },
  { article: "Sustainable Fashion", questions: 65 }
];

const AIInteractions: React.FC<AIInteractionsProps> = ({ timeframe }) => {
  return (
    <div className="space-y-6">
      <div className="grid gap-6 grid-cols-1 md:grid-cols-2">
        <Card className="dark:bg-gray-800 dark:text-gray-100">
          <CardHeader>
            <CardTitle>AI Question Categories</CardTitle>
            <CardDescription>Types of questions readers ask about your articles</CardDescription>
          </CardHeader>
          <CardContent>
            <ChartContainer
              config={{
                type: { 
                  theme: { light: '#F97316', dark: '#F97316' } 
                }
              }}
              className="h-64"
            >
              <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                  <Pie
                    data={questionTypeData}
                    cx="50%"
                    cy="50%"
                    labelLine={true}
                    outerRadius={80}
                    fill="#F97316"
                    dataKey="value"
                    nameKey="name"
                    label={({ name, percent }) => `${name}: ${(percent * 100).toFixed(0)}%`}
                  >
                    {questionTypeData.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={entry.color} />
                    ))}
                  </Pie>
                  <ChartTooltip
                    content={({ active, payload }) => {
                      if (active && payload && payload.length) {
                        return (
                          <ChartTooltipContent payload={payload} />
                        );
                      }
                      return null;
                    }}
                  />
                </PieChart>
              </ResponsiveContainer>
            </ChartContainer>
          </CardContent>
        </Card>

        <Card className="dark:bg-gray-800 dark:text-gray-100">
          <CardHeader>
            <CardTitle>Questions by Article Topic</CardTitle>
          </CardHeader>
          <CardContent>
            <ChartContainer
              config={{
                questions: { 
                  theme: { light: '#2563EB', dark: '#2563EB' } 
                }
              }}
              className="h-64"
            >
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={questionsByArticleData} layout="vertical">
                  <XAxis type="number" />
                  <YAxis type="category" dataKey="article" width={100} />
                  <Bar dataKey="questions" fill="var(--color-questions, #2563EB)" radius={[0, 4, 4, 0]} />
                  <ChartTooltip
                    formatter={(value) => [value, 'Questions']}
                    content={({ active, payload }) => {
                      if (active && payload && payload.length) {
                        return (
                          <div className="rounded-lg border bg-background p-2 shadow-sm">
                            <div className="grid grid-cols-2 gap-2">
                              <div className="font-medium">{payload[0].name}</div>
                              <div className="font-mono text-right">{payload[0].value}</div>
                            </div>
                          </div>
                        );
                      }
                      return null;
                    }}
                  />
                </BarChart>
              </ResponsiveContainer>
            </ChartContainer>
          </CardContent>
        </Card>
      </div>

      <Card className="dark:bg-gray-800 dark:text-gray-100">
        <CardHeader>
          <CardTitle>Most Popular Reader Questions</CardTitle>
          <CardDescription>
            Questions frequently asked to the AI about your articles
          </CardDescription>
        </CardHeader>
        <CardContent>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="w-[40%]">Question</TableHead>
                <TableHead className="w-[30%]">Article</TableHead>
                <TableHead className="text-right">Frequency</TableHead>
                <TableHead className="text-right">Satisfaction</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {topQuestionsData.map((item, index) => (
                <TableRow key={index}>
                  <TableCell className="font-medium">
                    <div className="flex items-start gap-2">
                      <MessageSquare className="h-4 w-4 text-muted-foreground mt-1 flex-shrink-0" />
                      <span>{item.question}</span>
                    </div>
                  </TableCell>
                  <TableCell>
                    <div className="flex items-start gap-2">
                      <FileText className="h-4 w-4 text-muted-foreground mt-1 flex-shrink-0" />
                      <span className="text-sm">{item.article}</span>
                    </div>
                  </TableCell>
                  <TableCell className="text-right">{item.count}</TableCell>
                  <TableCell className="text-right">
                    <div className="flex items-center justify-end gap-1">
                      <span className={item.satisfaction >= 90 ? "text-green-500" : item.satisfaction >= 80 ? "text-amber-500" : "text-red-500"}>
                        {item.satisfaction}%
                      </span>
                      {item.satisfaction >= 90 ? (
                        <ThumbsUp className="h-4 w-4 text-green-500" />
                      ) : item.satisfaction >= 80 ? (
                        <ThumbsUp className="h-4 w-4 text-amber-500" />
                      ) : (
                        <ThumbsDown className="h-4 w-4 text-red-500" />
                      )}
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      <Card className="dark:bg-gray-800 dark:text-gray-100">
        <CardHeader>
          <CardTitle>AI Insights for Content Creation</CardTitle>
          <CardDescription>
            Suggestions based on reader questions and interactions
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            <div className="flex items-start gap-3">
              <div className="w-8 h-8 rounded-full bg-directpress-orange flex items-center justify-center">
                <BrainCircuit className="h-4 w-4 text-white" />
              </div>
              <div>
                <h4 className="text-base font-medium">Knowledge Gaps</h4>
                <p className="text-sm text-muted-foreground">
                  Readers frequently ask about the specific economic impacts of climate change. Consider a more detailed follow-up article explaining economic models and sector-specific projections.
                </p>
              </div>
            </div>
            
            <div className="flex items-start gap-3">
              <div className="w-8 h-8 rounded-full bg-directpress-orange flex items-center justify-center">
                <BrainCircuit className="h-4 w-4 text-white" />
              </div>
              <div>
                <h4 className="text-base font-medium">Content Enhancement</h4>
                <p className="text-sm text-muted-foreground">
                  AI healthcare article generates high engagement but readers seek more technical details. Consider adding diagrams or interactive elements to explain complex AI diagnostic processes.
                </p>
              </div>
            </div>
            
            <div className="flex items-start gap-3">
              <div className="w-8 h-8 rounded-full bg-directpress-orange flex items-center justify-center">
                <BrainCircuit className="h-4 w-4 text-white" />
              </div>
              <div>
                <h4 className="text-base font-medium">New Article Opportunities</h4>
                <p className="text-sm text-muted-foreground">
                  Many readers ask comparative questions about cryptocurrency regulations. A new article comparing regulatory approaches across major financial markets would likely perform well.
                </p>
              </div>
            </div>
            
            <div className="flex items-start gap-3">
              <div className="w-8 h-8 rounded-full bg-directpress-orange flex items-center justify-center">
                <BrainCircuit className="h-4 w-4 text-white" />
              </div>
              <div>
                <h4 className="text-base font-medium">Reader Confusion</h4>
                <p className="text-sm text-muted-foreground">
                  Digital nomad article generates clarification questions about visa requirements. Consider updating with a more comprehensive section on international visa options.
                </p>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default AIInteractions;
